import React, { useState, useEffect } from "react";

import { NotificationManager } from "react-notifications";
import { useHistory, useParams } from "react-router-dom";
import CommonLoader from "../common/CommonLoader";
import {
  SaveWaterProfile,
  UpdateWaterProfile,
  GetWaterProfileById,
  CheckTypeAvailability, // New import for type availability checking
} from "../../services/ClientWaterProfileService";

const AddWaterProfile = ({accountNumber}) => {
  const history = useHistory();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    accountNumber: "",
    type: "",
    calcium: null,
    totalAlkalinity: null,
    bicarbonate: null,
    sulfate: null,
    magnesium: null,
    totalHardness: null,
    iron: null,
    pH: null,
    totalChlorine: null,
    residualAlkalinity: null,
    chloride: null,
    sulfateToChlorideRatio: null,
    sodium: null,
    zinc: null,
    potassium: null,
    copper: null,
    tds: null,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [checkingType, setCheckingType] = useState(false);
  const [typeValidationStatus, setTypeValidationStatus] = useState(null); // 'available', 'unavailable', or null

  useEffect(() => {    
    if (id && id!=="add") {
      fetchProfileData(parseInt(id, 10));
    }
    const value = accountNumber;
    setFormData((prev) => ({ ...prev, accountNumber: value }));
  }, [id]);

  useEffect(() => {
    const value = accountNumber;
    setFormData((prev) => ({ ...prev, accountNumber: value }));
  }, [accountNumber]);

  // New useEffect for type validation
  useEffect(() => {
    debugger
    if(String(formData.type)===""){
                setTypeValidationStatus(null);
              return
            }
    const checkTypeAvailability = async () => {
      if (id === "add" && accountNumber && formData.type.trim()) {
        setCheckingType(true);
        setTypeValidationStatus(null);
        
        try {
          // Add delay before API call
          const timeoutId = setTimeout(async () => {
            try {               
              const response = await CheckTypeAvailability({
                accountNumber: String(formData.accountNumber),
                type: String(formData.type),
              });
              
              if (response && response.statusCode === 200 && response.results === true) {
                setTypeValidationStatus('available');
                setErrors(prev => ({ ...prev, type: "" }));
              } else {
                setTypeValidationStatus('unavailable');
                setErrors(prev => ({ 
                  ...prev, 
                  type: "This type/water profile name is not available for this client. Please try a different name." 
                }));
              }
            } catch (error) {
              setTypeValidationStatus('unavailable');
              setErrors(prev => ({ 
                ...prev, 
                type: "Error checking type availability. Please try a different name." 
              }));
            } finally {
              setCheckingType(false);
            }
          }, 500); // 500ms delay

          return () => clearTimeout(timeoutId);
        } catch (error) {
          setCheckingType(false);
        }
      } else if (!accountNumber || !formData.type.trim()) {
        setTypeValidationStatus(null);
        setCheckingType(false);
      }
    };

    checkTypeAvailability();
  }, [accountNumber, formData.type, id]);

  const fetchProfileData = async (profileId) => {
    setLoading(true);
    try {      
      const response = await GetWaterProfileById(profileId);      
      if (response && response.results) {
        setFormData(response.results);
      } else {
        NotificationManager.error("Failed to fetch profile data");
      }
    } catch (error) {
      NotificationManager.error("Error fetching profile data");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.accountNumber)
      newErrors.accountNumber = "Account number is required";
    if (!formData.type) newErrors.type = "Type is required";
    
    // Don't allow saving if type is not available
    if (id === "add" && typeValidationStatus === 'unavailable') {
      newErrors.type = "This type/water profile name is not available for this client. Please try a different name.";
    }

    const numericFields = [
      "calcium",
      "totalAlkalinity",
      "bicarbonate",
      "sulfate",
      "magnesium",
      "totalHardness",
      "iron",
      "pH",
      "totalChlorine",
      "residualAlkalinity",
      "chloride",
      "sulfateToChlorideRatio",
      "sodium",
      "zinc",
      "potassium",
      "copper",
      "tds",
    ];

    numericFields.forEach((field) => {
      if (formData[field] && isNaN(formData[field])) {
        newErrors[field] = "Must be a valid number";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    // Additional check to prevent saving if type is still being validated or unavailable
    if (id === "add" && (checkingType || typeValidationStatus === 'unavailable')) {
      NotificationManager.warning("Please wait for type validation to complete or choose a different type name.");
      return;
    }

    const profileData = {
      ...formData,
      accountNumber: String(formData.accountNumber || ""),
      id: id ? parseInt(id, 10) : undefined,
    };

    setLoading(true);
    try {
      if (id!=="add") {
        await UpdateWaterProfile(profileData);
        NotificationManager.success("Profile updated successfully");
      } else {
        await SaveWaterProfile(profileData);
        NotificationManager.success("Profile added successfully");
      }
      history.push("/water-profiles");
    } catch (error) {
      NotificationManager.error(error.message || "Error saving profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value === "" ? null : Number(value) || value,
    }));
  };

  const handleTypeChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      type: value,
    }));
    
    // Reset validation status when user types
    if (value.trim()) {
      setTypeValidationStatus(null);
    }
  };

  const handleCancel = () => {
    history.push("/water-profiles");
  };

  // Helper function to get type field status
  const getTypeFieldStatus = () => {
    if (checkingType) return { color: 'info', text: 'Checking availability...' };
    if (typeValidationStatus === 'available') return { color: 'success', text: 'Type name is available' };
    if (typeValidationStatus === 'unavailable') return { color: 'danger', text: errors.type };
    return null;
  };

  const typeStatus = getTypeFieldStatus();

  return (
    <>
      <div className="box">
        <h1 className="title is-4 mb-5">
          {id && id !== "add" ? "Edit Water Profile" : "Add New Water Profile"}
        </h1>

        <div className="columns is-multiline">
          {/* Account Number and Type */}
          {id && id !== "add" ? (
            <>
              <div className="column is-3">
                <div className="content">
                  <strong>Account Number:</strong> {formData.accountNumber}
                </div>
              </div>
              <div className="column is-3">
                <div className="content">
                  <strong>Type:</strong> {formData.type}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="column is-3">
                <div className="content">
                  <strong>Account Number:</strong> {formData.accountNumber}
                </div>
              </div>
              <div className="column is-3">
                <div className="field">
                  <label className="label is-small">Water Profile Type *</label>
                  <div className="control has-icons-right">
                    <input
                      className={`input is-small ${
                        errors.type ? "is-danger" : 
                        typeStatus?.color === 'success' ? "is-success" : 
                        typeStatus?.color === 'info' ? "is-info" : ""
                      }`}
                      type="text"
                      value={formData.type}
                      onChange={handleTypeChange}
                      placeholder="Enter water profile type"
                      disabled={!accountNumber} // Disable until account is available
                    />
                    {checkingType && (
                      <span className="icon is-small is-right">
                        <i className="fas fa-spinner fa-pulse"></i>
                      </span>
                    )}
                  </div>
                  {typeStatus && (
                    <p className={`help is-${typeStatus.color}`}>
                      {typeStatus.text}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Chemical Properties Section */}
          <div className="column is-12">
            <h2 className="subtitle is-5 mt-4 mb-3">
              Chemical Properties (mg/L)
            </h2>
          </div>

          {[
            { field: "calcium", label: "Calcium" },
            { field: "totalAlkalinity", label: "Total Alkalinity" },
            { field: "bicarbonate", label: "Bicarbonate" },
            { field: "sulfate", label: "Sulfate" },
            { field: "magnesium", label: "Magnesium" },
            { field: "totalHardness", label: "Total Hardness" },
            { field: "iron", label: "Iron" },
            { field: "pH", label: "pH" },
            { field: "totalChlorine", label: "Total Chlorine" },
            { field: "residualAlkalinity", label: "Residual Alkalinity" },
            { field: "chloride", label: "Chloride" },
            {
              field: "sulfateToChlorideRatio",
              label: "Sulfate to Chloride Ratio",
            },
            { field: "sodium", label: "Sodium" },
            { field: "zinc", label: "Zinc" },
            { field: "potassium", label: "Potassium" },
            { field: "copper", label: "Copper" },
            { field: "tds", label: "TDS" },
          ].map(({ field, label }) => (
            <div className="column is-3" key={field}>
              <div className="field">
                <label className="label is-small">{label}</label>
                <div className="control">
                  <input
                    className={`input is-small ${
                      errors[field] ? "is-danger" : ""
                    }`}
                    type="number"
                    step="0.01"
                    value={formData[field] ?? ""}
                    onChange={handleChange(field)}
                    placeholder={`Enter ${label.toLowerCase()}`}
                  />
                </div>
                {errors[field] && (
                  <p className="help is-danger">{errors[field]}</p>
                )}
              </div>
            </div>
          ))}

          {/* Action Buttons */}
          <div className="column is-12">
            <div className="field is-grouped is-grouped-right mt-4">
              <div className="control">
                <button
                  className="button is-light"
                  onClick={handleCancel}
                  type="button"
                >
                  Cancel
                </button>
              </div>
              <div className="control">
                <button
                  className="button is-primary"
                  onClick={handleSave}
                  type="button"
                  disabled={id === "add" && (checkingType || typeValidationStatus === 'unavailable')}
                >
                  {id!=="add" ? "Update" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CommonLoader loading={loading} />
    </>
  );
};

export default AddWaterProfile;